import {
  PageDto,
  TableDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';

export type TableWidget = WidgetDto & {
  entity: TableDto;
};

function isTableWidget(widget: WidgetDto): widget is TableWidget {
  return widget.entity.type === 'table';
}

export function getAllTableWidgets(
  contents: PageDto['contents']
): TableWidget[] {
  const tableConfigs: TableWidget[] = [];
  for (const content of contents) {
    if (content.type === 'widget' && isTableWidget(content)) {
      tableConfigs.push(content);
    } else if (
      content.type === 'singleColumnGroup' ||
      content.type === 'twoColumnGroup'
    ) {
      tableConfigs.push(...getAllTableWidgets(content.contents));
    } else if (content.type === 'tabGroup') {
      for (const tab of content.contents) {
        tableConfigs.push(...getAllTableWidgets(tab.contents));
      }
    }
  }
  return tableConfigs;
}

/**
 * Returns the IDs of widgets that are currently visible on the page.
 * Widgets outside tab groups are always visible. Inside tab groups only
 * the active tab's widgets (or the first tab's when no active value is
 * known) are included.
 * @param activeTabValues - map of tabGroupId -> active tab id
 */
export function getVisibleWidgetIds(
  contents: PageDto['contents'],
  activeTabValues: Record<string, string>
): Set<string> {
  const ids = new Set<string>();
  for (const content of contents) {
    if (content.type === 'widget') {
      ids.add(content.id);
    } else if (
      content.type === 'singleColumnGroup' ||
      content.type === 'twoColumnGroup'
    ) {
      for (const id of getVisibleWidgetIds(content.contents, activeTabValues)) {
        ids.add(id);
      }
    } else if (content.type === 'tabGroup') {
      const activeTabValue = activeTabValues[content.id];
      const activeTab = activeTabValue
        ? content.contents.find((tab) => tab.id === activeTabValue)
        : content.contents[0];
      if (activeTab) {
        for (const id of getVisibleWidgetIds(
          activeTab.contents,
          activeTabValues
        )) {
          ids.add(id);
        }
      }
    }
  }
  return ids;
}
